import React, { useMemo, useRef, useState } from "react";

const getOptionText = (children) => {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(getOptionText).join("").trim();
  }

  if (React.isValidElement(children)) {
    return getOptionText(children.props.children);
  }

  return "";
};

function CustomSelect({
  name,
  value,
  defaultValue,
  onChange,
  onBlur,
  children,
  className = "",
  style,
  placeholder = "Select an option",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedValue = value ?? defaultValue ?? "";

  const options = useMemo(
    () =>
      React.Children.toArray(children)
        .filter((child) => React.isValidElement(child))
        .map((child) => {
          const label = getOptionText(child.props.children);
          const optionValue = child.props.value ?? label;

          return {
            disabled: Boolean(child.props.disabled),
            label,
            value: optionValue,
          };
        }),
    [children]
  );

  const selectedOption = options.find((option) => String(option.value) === String(selectedValue));
  const displayValue = selectedOption?.label || placeholder;

  const handleSelect = (option) => {
    if (option.disabled) return;

    onChange?.({
      target: {
        name,
        value: option.value,
      },
    });
    setOpen(false);
  };

  const handleBlur = (event) => {
    if (rootRef.current?.contains(event.relatedTarget)) return;

    onBlur?.({
      target: {
        name,
        value: selectedValue,
      },
    });
    setOpen(false);
  };

  return (
    <div className="custom-select" ref={rootRef} onBlur={handleBlur}>
      <button
        type="button"
        className={`input custom-select-trigger ${className}`}
        style={style}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span className={!selectedOption ? "custom-select-placeholder" : ""}>{displayValue}</span>
        <span className="custom-select-caret" aria-hidden="true" />
      </button>

      {open ? (
        <div className="custom-select-menu" role="listbox" tabIndex={-1}>
          {options
            .filter((option) => option.label || !option.disabled)
            .map((option) => (
              <button
                type="button"
                key={`${name}-${option.value}-${option.label}`}
                className={`custom-select-option ${
                  String(option.value) === String(selectedValue) ? "selected" : ""
                }`}
                disabled={option.disabled}
                role="option"
                aria-selected={String(option.value) === String(selectedValue)}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}

export default CustomSelect;
